import PropTypes from 'prop-types';
import React, { useMemo, useCallback, useState } from 'react';
import ContextMenuComponent from './ContextMenuComponent';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import { IconClose, IconPlus, IconWarning, IconSuggestion, IconDot } from 'svgIcons/MotionPortalIcons';
import { getTranslatedKPINameV2, translate } from 'helpers/translateHelper';
import { GeneralSelect } from 'sharedComponents/GeneralSelect';
import { useDispatch } from 'react-redux';
import { addLimit, removeSignal, updateSignal, discardLimit, handleSignalUpdate, postLimitRequest, cancel } from '../actions';
import { KPI_ERROR, KPI_TYPE, LIMIT_MENU_OPTIONS, TRENDS } from './constants';
import { getContextMenuOptions } from './helper';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { map as lmap, find as lfind, filter as lfilter } from 'lodash';
import OffsetModal from './OffestModal';
import { ASSET_TYPES_MS } from 'helpers/constants';
import { InfoButtonWithOnlineHelp } from 'sharedComponents/InfoButtonWithOnlineHelp';
import colors from 'theme/_colors.scss';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import ResetToDefaultModal from './ResetToDefaultModal';
import UnSavedChangesModal from './UnSavedChangesModal';

//TODO  : - : this will be removed when timeseriesKey is sent as part of limitConfig data
const getTimeSeriesKey = (kpiListData, keyName) => {
    const additionalTrendList = (kpiListData ?? [])?.filter(item => item?.timeseriesGroupName === 'AdditionalTrends');
    const keyObj = additionalTrendList[0]?.timeseries?.filter(item => item?.timeseriesKey === keyName); // USE timeseriesNameTextKey IF NOT WORK
    return keyObj[0]?.timeseriesKey || '';
};

const getPayload = (item, result, kpiList) => {
    const timeseries = result[0]?.timeseries[0];

    return {
        ...item,
        isTrend: result[0]?.timeseriesGroupCode === KPI_TYPE.Trend,
        timeseriesKey: result[0]?.timeseriesGroupCode === KPI_TYPE.Trend ? getTimeSeriesKey(kpiList, timeseries?.timeseriesKey) : timeseries?.timeseriesKey, // USE timeseriesNameTextKey IN getTimeSeriesKey IF NOT WORK
        trendKey: result[0]?.timeseriesGroupCode === KPI_TYPE.Trend ? timeseries?.timeseriesKey : '',
        displayNameTextKey: timeseries?.timeseriesNameTextKey,
        appendCodeToTranslation: timeseries?.appendCodeToTranslation,
    };
};

const renderLeftSection = (props, kpiOptionsMemo, handleTimeSeries, handleChange, handleUpdate, isAnyLimitsError) => {
    const isUserLimit = props.isUserLimit;
    const offsetValidation = props?.item?.offset !== undefined && props?.item?.offset !== null && props?.item?.offset !== '' && !isNaN(props?.item?.offset) && props?.item?.offset !== 0;
    return (
        <>
            <button
                className='limit-signal'
                onClick={(e) => props?.item?.displayName ? props?.handleExpandSignal(props?.item?.displayNameTextKey) : e.stopPropagation()}
                type='button'>
                {isUserLimit ?
                    <div>
                        {translate('ABB.Powertrain.Frontend.personalLimitsForNotification')}
                        <InfoButtonWithOnlineHelp infoText={translate('ABB.Powertrain.Frontend.personalLimitsInfoMessage')} />
                    </div> : <>
                        <GeneralSelect
                            options={kpiOptionsMemo}
                            onChange={(selected) => {
                                const unit = getMeasurementUnitSymbol(selected?.timeseriesUnitGroupID);
                                handleTimeSeries(selected.key, selected.title);
                                handleChange(selected, 'displayName');
                                handleUpdate('unit', unit);
                                handleUpdate('minValue', selected.minValue);
                                handleUpdate('maxValue', selected.maxValue);
                                handleUpdate('timeseriesUnitGroupID', selected.timeseriesUnitGroupID);
                                handleUpdate('isAdditionalLimitAllowed', selected.isAdditionalLimitAllowed);
                                handleUpdate('isDefaultLimitEditable', selected.isDefaultLimitEditable);
                                handleUpdate('isLimitEditable', selected.isLimitEditable);
                                props.handleExpandSignal(`${selected?.key}`);
                            }}
                            isDisabled={false}
                            getOptionLabel={(option) => option.title}
                            getOptionValue={(option) => option.id}
                            menuShouldScrollIntoView={false}
                            isSearchable={true}
                            disableOnSelect
                            selectedValue={props?.item?.displayName || ''}
                            error={props?.item?.isSignalError}
                            inputMaxChars={50}
                        />
                        {props?.item?.unit && <span className='limit-uom'>({props.item.unit})</span>}
                        {!isUserLimit && offsetValidation && <span className='limit-pipe'>|</span>}
                        {isAnyLimitsError && <div className='d-flex justify-content-center align-items-center ms-1'>
                            <IconWarning color={colors.red} width={16} height={16} />
                        </div>}
                    </>}
            </button>
            {!isUserLimit && offsetValidation && <div className='limit-offset'>
                {translate('ABB.Powertrain.Frontend.limitOffset')}: {props?.item?.offset > 0 ? `+${props?.item?.offset}` : props?.item?.offset}
            </div>}
        </>
    );
};

const renderRightSection = (props, handleAddLimit, handleDiscard, handleMenu, handleContextMenu) => {
    const isUserLimit = props.isUserLimit;
    const isAnyDefaultLimit = props.item?.limits?.some(limit => limit?.isDefault || limit?.isBasedOnDefault);

    const getTargetKey = () => !isUserLimit ? props?.item?.displayNameTextKey : `P_${props?.item?.displayNameTextKey}`;

    const shouldExpandSignal = () => {
        return !props?.expandedItem?.includes(props?.item?.displayNameTextKey) || !props?.expandedItem?.includes(`P_${props?.item?.displayNameTextKey}`);
    };

    const expandSignalIfNeeded = () => {
        if (shouldExpandSignal()) {
            props?.handleExpandSignal(getTargetKey());
        }
    };

    const contextMenuOptions = getContextMenuOptions(props, props.isApplicable ? props.item?.isOffsetEnabled : props.item?.offset, [
        { id: 'removeKpi', title: translate('ABB.Powertrain.Frontend.lblRemoveSignalBtn') },
        { id: 'editOffset', title: translate('ABB.Powertrain.Frontend.editOffset') },
        { id: 'reset default limits', title: translate('ABB.Powertrain.Frontend.ResetDefault') }
    ]);

    const renderNotificationStatus = () => {
        if (isUserLimit) {
            return (
                <>
                    {props.notificationCount} {translate('ABB.Powertrain.Frontend.activeNotifyMeLimits')?.toLowerCase()}
                    <div className='ms-2 dot'>
                        {props.notificationCount > 0 ? <IconDot /> : <IconDot color={colors.doveGray} />}
                    </div>
                </>
            );
        }

        return (
            <>
                {props?.anyLimitEventActive
                    ? translate('ABB.Powertrain.Frontend.activeNotifyMeLimits')
                    : translate('ABB.Powertrain.Frontend.inActiveNotifyMeLimits')
                }
                <div className='ms-2 dot'>
                    <IconDot color={props?.anyLimitEventActive ? undefined : colors.doveGray} />
                </div>
            </>
        );
    };

    const handleAddLimitClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleContextMenu(null);
        if (e.currentTarget.classList.contains('add-limit-disabled')) {
            return;
        }
        expandSignalIfNeeded();
        handleAddLimit(props?.item?.uniqId);
    };

    const getAddLimitClassName = () => {
        const baseClass = 'add-limit';
        const hasLimitsOrUser = !props?.timeseriesLoading && props?.defaultLimits?.length > 0 || isUserLimit;
        const isSmseDisabled = props?.checkIsSmseAsset && !isUserLimit;
        const isCustomLimitDisabled = !props?.checkIsSmseAsset && !isUserLimit &&
            (props?.item?.isAddingCustomLimitsAllowed === false || props?.defaultLimits?.length >= 4);

        if (!hasLimitsOrUser || isSmseDisabled || isCustomLimitDisabled) {
            return `${baseClass} add-limit-disabled`;
        }
        return baseClass;
    };

    const renderSuggestButton = () => {
        const shouldShowSuggest = props?.item?.timeseriesKey &&
            props?.asset?.assetType === ASSET_TYPES_MS?.MOTOR &&
            !isUserLimit &&
            isAnyDefaultLimit;

        if (!shouldShowSuggest) { return null; }

        return (
            <button
                className='ms-2 me-1'
                onClick={e => {
                    e.stopPropagation();
                    handleContextMenu(null);
                    expandSignalIfNeeded();
                    props?.openSuggestLimitModal();
                }}
                type='button'>
                <IconSuggestion /> {translate('ABB.Powertrain.Frontend.suggestTitle')}
            </button>
        );
    };

    const renderContextMenu = () => {
        if (isUserLimit) { return null; }
        return (
            <button onClick={e => e.stopPropagation()} type='button'>
                <ContextMenuComponent
                    options={contextMenuOptions}
                    onSelect={handleMenu}
                    id={props?.item?.uniqId}
                    isOpen={props?.item?.uniqId === props.contextMenuId}
                    setContextMenuId={handleContextMenu}
                />
            </button>
        );
    };

    if (!props.isApplicable && props.checkIsSmseAsset) {
        return renderContextMenu();
    }

    return (
        <>
            <div className='active-notification-count'>
                {renderNotificationStatus()}
            </div>

            {(props.timeseriesLoading || props.item?.isModified && props.showDiscard) &&
                <GeneralButton
                    type='discreet'
                    icon={IconClose({ width: '14px', height: '14px', color: '#1f1f1f' })}
                    text={'Discard'}
                    className='limit-discard'
                    onClick={(e) => {
                        e.stopPropagation();
                        handleContextMenu(null);
                        handleDiscard(props?.item?.uniqId);
                    }}
                />
            }

            {renderSuggestButton()}

            <div
                className={getAddLimitClassName()}
                onClick={handleAddLimitClick}
                tabIndex={0}
                role='button'
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleAddLimitClick(e);
                    }
                }}
            >
                <IconPlus />
                <div>{translate('ABB.Powertrain.Frontend.addLimit')}</div>
            </div>

            {renderContextMenu()}
        </>
    );
};

const checkLimitsError = (item) => {
    return (
        item?.isSignalError ||
        item?.limits?.some(
            (lmt) =>
                lmt?.isSameLimit ||
                !lmt?.limitDirection ||
                lmt?.limitValue === undefined ||
                lmt.limitValue === null ||
                lmt.limitValue === ''
        )
    );
};

const generateKpiOptions = (props) => {
    const kpiList = props?.model?.limitKPI?.kpiList ?? [];
    kpiList.forEach((item) => {
        if (item?.timeseriesGroupName === TRENDS) {
            item?.timeseries?.sort((a, b) => a?.timeseriesKey?.localeCompare(b?.timeseriesKey));
        }
    });

    const accumulatedKPIItems = (kpiList ?? []).reduce((acc, item) => {
        item?.timeseries?.forEach((series) => {
            const title = getTranslatedKPINameV2(series);
            const id = getTranslatedKPINameV2(series);
            const key = series?.timeseriesNameTextKey;
            const minValue = series?.minValue;
            const maxValue = series?.maxValue;
            const uom = series?.unit;
            const timeseriesUnitGroupID = series?.timeseriesUnitGroupID;
            const timeseriesKey = series?.timeseriesKey;
            const isAdditionalLimitAllowed = series?.isAdditionalLimitAllowed;
            const isLimitEditable = series?.isLimitEditable;
            const isDefaultLimitEditable = series?.isDefaultLimitEditable;

            acc.push({ title, id, key, minValue, maxValue, uom, isLimitEditable, timeseriesUnitGroupID, timeseriesKey, isAdditionalLimitAllowed, isDefaultLimitEditable });
        });

        return acc;
    }, []);

    const accumulatedKeys = new Set(props?.model?.currentTimeSeries?.map((item) => item?.displayNameTextKey));
    const filteredTimeseries = accumulatedKPIItems?.filter((series) => !accumulatedKeys.has(series?.key));

    return filteredTimeseries;
};

const LimitTitle = ({ showOffset, setShowOffset, ...props }) => {
    const dispatch = useDispatch();
    const [showModal, setShowModal] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const isUserLimit = props.isUserLimit;

    const handleChange = useCallback((selected, field) => {
        if (props?.item?.isSignalError) {
            dispatch(updateSignal(props?.item?.uniqId, KPI_ERROR.isSignalError, false));
        }
        dispatch(updateSignal(props?.item?.uniqId, field, selected.title));
    }, [dispatch, props?.item]);

    const handleUpdate = useCallback((field, value) => {
        dispatch(updateSignal(props?.item?.uniqId, field, value));
    }, [dispatch, props?.item]);

    const handleAddLimit = useCallback((uniqId) => {
        if (props?.defaultLimits?.length >= 3 && !isUserLimit) {
            props.setShowMaxLimitMessage(true);
        }
        dispatch(addLimit(uniqId, isUserLimit));
    }, [dispatch, props?.defaultLimits, isUserLimit]);

    const handleDiscard = useCallback((uniqId) => {
        if (props?.item?.limits?.length) {
            dispatch(discardLimit(uniqId, isUserLimit));
        } else {
            dispatch(cancel(uniqId));
        }
    }, [dispatch, props?.item, isUserLimit]);

    const handleRemoveSignal = useCallback(() => {
        if (props?.item?.timeseriesKey) {
            const payload = [{ ...props?.item, limits: [], offset: null }];
            dispatch(postLimitRequest(props?.asset?.id, payload, true));
        } else {
            dispatch(removeSignal(props?.item?.uniqId));
        }
    }, [dispatch, props?.item, props?.asset]);

    const handleDefaultLimit = useCallback(() => {
        setShowReset(true);
    }, []);

    const handleTimeSeries = useCallback((value) => {
        const data = lfilter(props?.model?.limitKPI?.kpiList ?? [], item =>
            lfind(item?.timeseries, series => series?.timeseriesNameTextKey === value)
        );

        const result = lmap(data, obj => ({
            ...obj,
            timeseries: lfilter(obj?.timeseries, series => series?.timeseriesNameTextKey === value)
        }));

        const payload = getPayload(props?.item, result, props?.model?.limitKPI?.kpiList);
        dispatch(handleSignalUpdate(payload));
    }, [dispatch, props?.model?.limitKPI?.kpiList, props?.item]);

    const kpiOptionsMemo = useMemo(() => generateKpiOptions(props), [props]);

    const isAnyLimitsError = useMemo(() => checkLimitsError(props?.item), [props?.item]);

    const handleMenu = useCallback((value) => {
        switch (value) {
            case LIMIT_MENU_OPTIONS.REMOVE_SIGNAL:
                setShowModal(true);
                break;
            case LIMIT_MENU_OPTIONS.EDIT_OFFSET:
                setShowOffset(props?.item?.uniqId);
                break;
            case LIMIT_MENU_OPTIONS.RESET_DEFAULT_LIMITS:
                handleDefaultLimit();
                break;
            default:
                break;
        }
    }, [props?.item, handleDefaultLimit]);

    const handleContextMenu = useCallback((uniqId) => {
        props.setContextMenuId(uniqId);
    }, [props.setContextMenuId]);

    const renderResetDialog = () => {
        if (!showReset) { return null; }
        return (
            <button onClick={(e) => e.stopPropagation()} type='button'>
                {props.hasModifiedItem ? <UnSavedChangesModal
                    showModal={showReset}
                    setShowModal={setShowReset}
                    isResetToDefault={true}
                /> : <ResetToDefaultModal {...props} showReset={showReset} setShowReset={setShowReset} />}
            </button>
        );
    };

    const renderRemoveSignalDialog = () => {
        if (!showModal) { return null; }
        return (
            <button onClick={(e) => e.stopPropagation()} type='button'>
                <GeneralDialog
                    show={showModal}
                    close={() => setShowModal(false)}
                    notificationType='info'
                    title={translate('ABB.Powertrain.Frontend.lblRemoveSignal')}
                    cancelButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.cancelLabel'),
                        onClick: () => setShowModal(false),
                    }}
                    acceptButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.lblRemove'),
                        onClick: handleRemoveSignal,
                    }}
                    closeButton={true}
                    persistent={false}
                >
                    <div
                        className='remove-signal-text'
                        dangerouslySetInnerHTML={{
                            __html: translate('ABB.Powertrain.Frontend.limitRemoval'),
                        }}
                    ></div>
                </GeneralDialog>
            </button>
        );
    };

    return (
        <div className='limit-title-container'>
            {renderResetDialog()}
            {renderRemoveSignalDialog()}
            {showOffset === props.item?.uniqId && <button onClick={(e) => e.stopPropagation()} type='button'>
                <OffsetModal showOffset={showOffset} setShowOffset={setShowOffset} item={props?.item} />
            </button>}
            <div className='limit-title-left'>
                {renderLeftSection(props, kpiOptionsMemo, handleTimeSeries, handleChange, handleUpdate, isAnyLimitsError)}
            </div>
            <div className='limit-title-right'>
                {renderRightSection(props, handleAddLimit, handleDiscard, handleMenu, handleContextMenu)}
            </div>
        </div>
    );
};

LimitTitle.propTypes = {
    item: PropTypes.shape({
        uniqId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // Unique identifier for the item
        displayName: PropTypes.string, // Display name of the item
        displayNameTextKey: PropTypes.string, // Text key for the display name
        isSignalError: PropTypes.bool, // Whether the signal has an error
        isOffsetEnabled: PropTypes.bool, // Whether the offset is enabled
        offset: PropTypes.number, // Offset value
        isModified: PropTypes.bool, // Whether the item is modified
        timeseriesKey: PropTypes.string, // Timeseries key
        unit: PropTypes.string, // Unit of measurement
        minValue: PropTypes.number, // Minimum value
        maxValue: PropTypes.number, // Maximum value
        isAddingCustomLimitsAllowed: PropTypes.bool, // Whether adding custom limits is allowed
        limits: PropTypes.arrayOf(
            PropTypes.shape({
                isSameLimit: PropTypes.bool, // Whether the limit is the same as another
                limitDirection: PropTypes.string, // Direction of the limit
                limitValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), // Value of the limit
            })
        ),
    }),
    asset: PropTypes.shape({
        id: PropTypes.string, // Asset ID
        assetType: PropTypes.string, // Type of the asset
    }),
    model: PropTypes.shape({
        limitKPI: PropTypes.shape({
            kpiList: PropTypes.arrayOf(
                PropTypes.shape({
                    timeseries: PropTypes.arrayOf(
                        PropTypes.shape({
                            timeseriesNameTextKey: PropTypes.string, // Text key for the timeseries name
                            minValue: PropTypes.number, // Minimum value
                            maxValue: PropTypes.number, // Maximum value
                            unit: PropTypes.string, // Unit of measurement
                            isDefaultLimitEditable: PropTypes.bool, // Whether the default limit is editable
                        })
                    ),
                })
            ),
        }),
        currentTimeSeries: PropTypes.arrayOf(
            PropTypes.shape({
                displayNameTextKey: PropTypes.string, // Text key for the display name
            })
        ),
    }),
    contextMenuId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // ID of the currently open context menu
    setContextMenuId: PropTypes.func, // Function to set the context menu ID
    handleExpandSignal: PropTypes.func, // Function to handle signal expansion
    openSuggestLimitModal: PropTypes.func, // Function to open the suggest limit modal
    defaultLimits: PropTypes.arrayOf(PropTypes.object), // Array of default limits
    expandedItem: PropTypes.arrayOf(PropTypes.string), // Array of expanded items
    isUserLimit: PropTypes.bool, // Whether the limit is user-defined
    isAnySignalModified: PropTypes.bool, // Whether any signal is modified
    notificationCount: PropTypes.number, // Count of active notifications
    anyLimitEventActive: PropTypes.bool, // Whether any limit event is active
    timeseriesLoading: PropTypes.bool, // Whether the timeseries data is loading
    showDiscard: PropTypes.bool, // Whether to show the discard button
    checkIsSmseAsset: PropTypes.bool, // Whether the asset is an SMSE asset
    setShowMaxLimitMessage: PropTypes.func, // Function to show the max limit message
    showOffset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setShowOffset: PropTypes.func,
    hasModifiedItem: PropTypes.bool,
    isApplicable: PropTypes.bool, // Whether the item is applicable
};

export default React.memo(LimitTitle);
